import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Box,
  Paper,
  Divider,
  CircularProgress,
  Avatar,
  Chip,
  Alert,
  styled,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  getDriverDetails,
  verifyDriverDocument,
} from '../../services/DriverService.ts';
import { DOC_TYPE, STATUS, TDriverDetails } from './type.ts';
import VerifiedTick from '../../types/components/shared/VerifiedLabel.tsx';
import VehicleCard from './VehicleList.tsx';
import DocumentView from './DocumentView/index.tsx';
import VerifyBox from './DocumentView/VerifyBox.tsx';
import { Verified, ErrorOutline, PendingActions } from '@mui/icons-material';

// Styled components
const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const ProfileContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(3),
  position: 'relative',
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  border: `4px solid ${theme.palette.background.paper}`,
  boxShadow: theme.shadows[3],
  marginBottom: theme.spacing(2),
}));

const InfoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(1.5),
  '& .MuiTypography-root': {
    marginRight: theme.spacing(1),
  },
}));

const CustomAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  boxShadow: theme.shadows[2],
  '&:before': {
    display: 'none',
  },
}));

const CustomAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
  '&.Mui-expanded': {
    minHeight: 48,
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
  },
}));

const DriverProfileModal = ({ open, onClose, driverId }) => {
  const theme = useTheme();
  const [driverData, setDriverData] = useState<TDriverDetails>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileVerified, setProfilePicVerifed] = useState(false);
  const [expanded, setExpanded] = useState('driver-panel');

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    if (driverId && open) {
      // Fetch driver data when modal opens
      const fetchDetails = async () => {
        try {
          const info = await getDriverDetails(driverId);
          const details: TDriverDetails = info.data;

          setDriverData(details);
          setProfilePicVerifed(
            details.verificationStatus?.profile_photo_status === STATUS.VERIFIED
          );
          setLoading(false);
        } catch (err) {
          setError('Failed to load driver details');
          setLoading(false);
        }
      };
      fetchDetails();
    }
  }, [driverId, open]);

  const handleClose = () => {
    onClose();
    setDriverData(undefined);
    setError(null);
    setLoading(true);
    setExpanded('driver-panel');
  };

  const verifyDocumentDetails = (
    document_type: DOC_TYPE,
    status: STATUS,
    rejection_message: string
  ) => {
    return verifyDriverDocument(
      driverId,
      document_type,
      status,
      rejection_message
    );
  };

  const profilePicverify = async (
    status: STATUS,
    rejection_message: string
  ) => {
    const isVerifed = await verifyDocumentDetails(
      DOC_TYPE.PROFILE_PHOTO,
      status,
      rejection_message
    );
    setProfilePicVerifed(isVerifed ?? false);
  };

  // Status badge component for the profile
  const StatusBadge = ({ status }) => {
    const getStatusIcon = () => {
      switch (status) {
        case STATUS.VERIFIED:
          return <Verified fontSize="small" />;
        case STATUS.REJECTED:
          return <ErrorOutline fontSize="small" />;
        default:
          return <PendingActions fontSize="small" />;
      }
    };

    const getStatusLabel = () => {
      switch (status) {
        case STATUS.VERIFIED:
          return 'Verified';
        case STATUS.REJECTED:
          return 'Rejected';
        case STATUS.VERIFICATION_PENDING:
          return 'Pending Verification';
        case STATUS.UPLOADED:
          return 'Uploaded';
        case STATUS.PENDING_UPLOAD:
          return 'Not Uploaded';
        default:
          return 'Unknown';
      }
    };

    // Get colors based on status
    const getStatusStyles = () => {
      switch (status) {
        case STATUS.VERIFIED:
          return {
            bgColor: theme.palette.success.light,
            textColor: theme.palette.success.contrastText
          };
        case STATUS.REJECTED:
          return {
            bgColor: theme.palette.error.light,
            textColor: theme.palette.error.contrastText
          };
        case STATUS.VERIFICATION_PENDING:
          return {
            bgColor: theme.palette.warning.light,
            textColor: theme.palette.warning.contrastText
          };
        case STATUS.UPLOADED:
          return {
            bgColor: theme.palette.info.light,
            textColor: theme.palette.info.contrastText
          };
        case STATUS.PENDING_UPLOAD:
          return {
            bgColor: theme.palette.grey[300],
            textColor: theme.palette.text.primary
          };
        default:
          return {
            bgColor: theme.palette.grey[300],
            textColor: theme.palette.text.primary
          };
      }
    };

    const styles = getStatusStyles();

    return (
      <Chip
        icon={getStatusIcon()}
        label={getStatusLabel()}
        size="small"
        sx={{
          marginLeft: theme.spacing(1),
          backgroundColor: styles.bgColor,
          color: styles.textColor
        }}
      />
    );
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <StyledDialogTitle>
          <Typography variant="h6">Driver Profile</Typography>
        </StyledDialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading driver information...
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <StyledDialogTitle>
          <Typography variant="h6">Driver Profile</Typography>
        </StyledDialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth PaperProps={{ 
      elevation: 5,
      sx: { borderRadius: 2 }
    }}>
      <StyledDialogTitle>
        <Typography variant="h6">Driver Profile</Typography>
      </StyledDialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {driverData && (
          <Box>
            {/* Profile Section */}
            <Paper elevation={0} sx={{ mb: 3 }}>
              <ProfileContainer>
                <ProfileAvatar src={driverData.driver.profile_photo} alt={driverData.driver.name}>
                  {!driverData.driver.profile_photo && driverData.driver.name?.charAt(0)}
                </ProfileAvatar>
                
                {profileVerified && (
                  <Box 
                    sx={{ 
                      position: 'absolute',
                      top: 24,
                      right: 24,
                      bgcolor: 'success.main',
                      color: 'white',
                      borderRadius: '50%',
                      width: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Verified />
                  </Box>
                )}
                
                <Typography variant="h5" gutterBottom>
                  {driverData.driver.name}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Chip 
                    label={driverData.driver.phone} 
                    variant="outlined" 
                    size="small" 
                    sx={{ mr: 1 }} 
                  />
                  
                  {driverData.driver.email && (
                    <Chip 
                      label={driverData.driver.email} 
                      variant="outlined" 
                      size="small" 
                      sx={{ mr: 1 }} 
                      icon={driverData.driver.isEmailVerified ? <Verified fontSize="small" /> : undefined}
                    />
                  )}
                  
                  <Chip 
                    label={`★ ${driverData.driver.average_rating.toFixed(1)}`} 
                    size="small" 
                    color="primary" 
                  />
                </Box>
                
                {driverData.driver.current_location && (
                  <Typography variant="body2" color="textSecondary">
                    📍 {driverData.driver.current_location}
                  </Typography>
                )}
              </ProfileContainer>
              
              {driverData.verificationStatus?.profile_photo_status === STATUS.VERIFICATION_PENDING && (
                <Box sx={{ px: 3, pb: 3 }}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <VerifyBox
                      onVerify={profilePicverify}
                      label="Verify Profile Picture"
                      reason={driverData.verificationStatus?.profile_photo_rejection_message}
                    />
                  </Paper>
                </Box>
              )}
              
              {driverData.verificationStatus?.profile_photo_status === STATUS.REJECTED && (
                <Box sx={{ px: 3, pb: 3 }}>
                  <Alert severity="warning">
                    <Typography variant="body2">
                      <strong>Profile Picture Rejected:</strong> {driverData.verificationStatus?.profile_photo_rejection_message}
                    </Typography>
                  </Alert>
                </Box>
              )}
            </Paper>

            {/* Accordion Sections */}
            <Box sx={{ px: 3, pb: 3 }}>
              {/* Driver Details */}
              <CustomAccordion 
                expanded={expanded === 'driver-panel'} 
                onChange={handleChange('driver-panel')}
              >
                <CustomAccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="driver-info-content"
                  id="driver-info-header"
                >
                  <SectionTitle variant="h6">
                    Driver Details
                  </SectionTitle>
                </CustomAccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <InfoItem>
                        <Typography variant="subtitle2">Name:</Typography>
                        <Typography variant="body1">{driverData.driver.name}</Typography>
                      </InfoItem>
                      
                      <InfoItem>
                        <Typography variant="subtitle2">Date of Birth:</Typography>
                        <Typography variant="body1">{driverData.driver.dob || 'N/A'}</Typography>
                      </InfoItem>
                      
                      <InfoItem>
                        <Typography variant="subtitle2">Gender:</Typography>
                        <Typography variant="body1">{driverData.driver.gender}</Typography>
                      </InfoItem>
                      
                      <InfoItem>
                        <Typography variant="subtitle2">Phone:</Typography>
                        <Typography variant="body1">{driverData.driver.phone}</Typography>
                      </InfoItem>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <InfoItem>
                        <Typography variant="subtitle2">Email:</Typography>
                        <Typography variant="body1">
                          {driverData.driver.email || 'N/A'}
                          {driverData.driver.isEmailVerified && <VerifiedTick />}
                        </Typography>
                      </InfoItem>
                      
                      <InfoItem>
                        <Typography variant="subtitle2">Driving License No:</Typography>
                        <Typography variant="body1">{driverData.driver.driving_license_no || 'N/A'}</Typography>
                      </InfoItem>
                      
                      <InfoItem>
                        <Typography variant="subtitle2">License Expiry:</Typography>
                        <Typography variant="body1">{driverData.driver.license_expiry || 'N/A'}</Typography>
                      </InfoItem>
                      
                      <InfoItem>
                        <Typography variant="subtitle2">Vehicle ID:</Typography>
                        <Typography variant="body1">{driverData.driver.vehicleId}</Typography>
                      </InfoItem>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>Verification Status:</Typography>
                      
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ mr: 1 }}>Profile Photo:</Typography>
                            <StatusBadge status={driverData.verificationStatus?.profile_photo_status} />
                          </Box>
                        </Grid>
                        
                        <Grid item>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ mr: 1 }}>License:</Typography>
                            <StatusBadge status={driverData.verificationStatus?.license_status} />
                          </Box>
                        </Grid>
                        
                        <Grid item>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ mr: 1 }}>License Number:</Typography>
                            <StatusBadge status={driverData.verificationStatus?.license_number_status} />
                          </Box>
                        </Grid>
                        
                        <Grid item>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ mr: 1 }}>Registration Certificate:</Typography>
                            <StatusBadge status={driverData.verificationStatus?.rc_status} />
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </CustomAccordion>

              {/* Vehicle Details */}
              <CustomAccordion 
                expanded={expanded === 'vehicle-panel'} 
                onChange={handleChange('vehicle-panel')}
              >
                <CustomAccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="vehicle-info-content"
                  id="vehicle-info-header"
                >
                  <SectionTitle variant="h6">
                    Vehicle Details
                  </SectionTitle>
                </CustomAccordionSummary>
                <AccordionDetails>
                  <VehicleCard vehicles={driverData.driver.vehicles} />
                </AccordionDetails>
              </CustomAccordion>

              {/* Documents */}
              <CustomAccordion 
                expanded={expanded === 'documents-panel'} 
                onChange={handleChange('documents-panel')}
              >
                <CustomAccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="documents-content"
                  id="documents-header"
                >
                  <SectionTitle variant="h6">
                    Documents
                  </SectionTitle>
                </CustomAccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                        <DocumentView
                          onVerify={async (
                            status: STATUS,
                            rejection_message: string
                          ) => {
                            const validLicence = verifyDocumentDetails(
                              DOC_TYPE.LICENSE,
                              status,
                              rejection_message
                            );
                            const validLicenceNum = verifyDocumentDetails(
                              DOC_TYPE.LICENSE_NUMBER,
                              status,
                              rejection_message
                            );

                            return (
                              ((await validLicence) && (await validLicenceNum)) ??
                              false
                            );
                          }}
                          documentName="Driving License"
                          documentNumber={driverData.driver.driving_license_no || ''}
                          status={
                            driverData.verificationStatus?.license_number_status
                          }
                          reason={
                            driverData.verificationStatus
                              ?.license_rejection_message ??
                            driverData.verificationStatus
                              ?.license_number_rejection_message
                          }
                          fileUrl={driverData.driver.driving_license}
                        />
                      </Paper>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <DocumentView
                          onVerify={(status: STATUS, rejection_message: string) =>
                            verifyDocumentDetails(
                              DOC_TYPE.RC,
                              status,
                              rejection_message
                            )
                          }
                          reason={
                            driverData.verificationStatus?.rc_rejection_message
                          }
                          documentName="Registration Certificate"
                          documentNumber={driverData.driver.vehicles[0]?.vehicle_no || ''}
                          status={driverData.verificationStatus?.rc_status}
                          fileUrl={driverData.driver.registration_certificate}
                        />
                      </Paper>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </CustomAccordion>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={handleClose} 
          variant="outlined" 
          color="primary"
          size="large"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DriverProfileModal;